import { FieldErrors, useFormContext } from 'react-hook-form'
import { AuftragOption, AuftragOptionen, BefehlFormField } from '../Generic'
import AuftragCheckbox from '../Generic/AuftragCheckbox'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo } from 'react'
import { BetriebsstelleField, BetriebsstellenartField, Input } from '../../form'
import { Befehl24FormData } from './schema'
import BorderedBox from '../Generic/BorderedBox'
import ZugmeldestelleWrapper from '../Generic/ZugmeldestelleWrapper'

export interface Auftrag24_11_12FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings: boolean
  errors?: FieldErrors
}

export default function Auftrag24_11_12Form({
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings,
  errors
}: Readonly<Auftrag24_11_12FormProps>) {
  const { t } = useTranslation('ui-befehle')

  const { control, formState, watch, setValue } =
    useFormContext<Befehl24FormData>()

  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const auftrag_24_11_enabled = watch('auftrag_24_11.enabled')
  const auftrag_24_12_enabled = watch('auftrag_24_12.enabled')
  const sel24_13Betriebsstellenart = watch(
    'auftrag_24_11_12_fields.feld24_13.art'
  )
  const sel24_14Betriebsstellenart = watch(
    'auftrag_24_11_12_fields.feld24_14.art'
  )
  const feld_24_15_16_type = watch('auftrag_24_11_12_fields.feld_24_15_16_type')

  useEffect(() => {
    if (auftrag_24_11_enabled) {
      setValue('auftrag_24_12.enabled', false)
    }
  }, [auftrag_24_11_enabled, setValue])

  useEffect(() => {
    if (auftrag_24_12_enabled) {
      setValue('auftrag_24_11.enabled', false)
    }
  }, [auftrag_24_12_enabled, setValue])

  const clearConflicting24_15_16Fields = useCallback(
    (newType: string) => {
      if (newType === 'km') {
        setValue('auftrag_24_11_12_fields.feld_24_16_signal', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      } else if (newType === 'signal') {
        setValue('auftrag_24_11_12_fields.feld_24_15_km', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      }
    },
    [setValue]
  )

  const on24_15_16TypeChange = useCallback(
    (newValue: string) => {
      setValue(
        'auftrag_24_11_12_fields.feld_24_15_16_type',
        newValue as '' | 'km' | 'signal'
      )
      clearConflicting24_15_16Fields(newValue)
    },
    [setValue, clearConflicting24_15_16Fields]
  )

  return (
    <div
      className="flex px-1.75 items-baseline"
      data-testid={`${testId}-main-div`}
    >
      <BefehlFormField
        key={
          !readOnly || auftrag_24_11_enabled
            ? 'auftrag_24_11.enabled'
            : 'auftrag_24_12.enabled'
        }
        name={
          !readOnly || auftrag_24_11_enabled
            ? 'auftrag_24_11.enabled'
            : 'auftrag_24_12.enabled'
        }
        control={control}
        label=""
        readOnly={readOnly}
        activateEmptyWarning={activateEmptyFieldWarnings}
        data-testid={
          !readOnly || auftrag_24_11_enabled
            ? `${testId}-auftrag-24-11`
            : `${testId}-auftrag-24-12`
        }
        render={({ field }) => (
          <div className="flex items-baseline gap-0.5">
            <div className="flex justify-center items-start pt-0.25">
              <AuftragCheckbox
                checked={field.value}
                label={!readOnly || auftrag_24_11_enabled ? '24.11' : '24.12'}
                onChange={(e) => field.onChange(e.target.checked)}
                readOnly={readOnly}
                data-testid={
                  !readOnly || auftrag_24_11_enabled
                    ? `${testId}-auftrag-24-11-inner`
                    : `${testId}-auftrag-24-12-inner`
                }
                errors={fieldErrors}
              />
            </div>
          </div>
        )}
      />

      <div
        className="flex flex-wrap gap-1 items-baseline"
        data-testid={`${testId}-main-div`}
      >
        {(!readOnly || auftrag_24_11_enabled) && (
          <div data-testid={`${testId}-auftrag-24-11-text`} className="px-0.5">
            {t('auftraege.24_11.label')}
          </div>
        )}
        {readOnly && auftrag_24_12_enabled && (
          <div data-testid={`${testId}-auftrag-24-12-text`} className="px-0.5">
            {t('auftraege.24_11.24_12')}
          </div>
        )}
        <div
          className="flex items-baseline flex-wrap gap-1"
          data-testid={`${testId}-auftrag-24-11-wrapper`}
        >
          {!readOnly && (
            <span data-testid={`${testId}-text-oder`}>
              {t('auftraege.24_11.oder')}
            </span>
          )}
          {!readOnly && (
            <BefehlFormField
              name="auftrag_24_12.enabled"
              control={control}
              label=""
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              data-testid={`${testId}-auftrag-24-12`}
              render={({ field }) => (
                <div className="flex items-baseline gap-0.5">
                  <div className="flex justify-center items-start pt-0.25">
                    <AuftragCheckbox
                      checked={field.value}
                      label={`24.12`}
                      onChange={(e) => field.onChange(e.target.checked)}
                      readOnly={readOnly}
                      data-testid={`${testId}-auftrag-24-12-inner`}
                      errors={fieldErrors}
                    />
                  </div>
                  <span data-testid={`${testId}-auftrag-24-12-text`}>
                    {t('auftraege.24_11.24_12')}
                  </span>
                </div>
              )}
            />
          )}
        </div>

        <div
          data-testid={`${testId}-grid-layout-wrapper`}
          className="grid grid-cols-1 lg:grid-cols-[0.9fr_5fr] content-start w-full gap-1 items-baseline"
        >
          <div data-testid={`${testId}-text-von-div`} className="lg:text-right">
            {' '}
            <span data-testid={`${testId}-text-von-span`}>
              {t('auftraege.24_11.von')}
            </span>
          </div>
          <div data-testid={`${testId}-feld-24-13-div`}>
            <BorderedBox
              data-testid={`${testId}-feld-24-13-bordered-box`}
              footer={
                <span data-testid={`${testId}-feld-24-13-label`}>
                  {t('auftraege.24_11.24_13_zugmeldestelle')}
                </span>
              }
            >
              <ZugmeldestelleWrapper
                betriebsstellenartField={
                  <BefehlFormField
                    name="auftrag_24_11_12_fields.feld24_13.art"
                    control={control}
                    label="Betriebsstellenart"
                    readOnly={readOnly}
                    disabled={false}
                    activateEmptyWarning={activateEmptyFieldWarnings}
                    useTextDisplayInReadOnly={true}
                    data-testid={`${testId}-feld-24-13-art`}
                    render={({ field, isDisabled, isReadOnly }) => (
                      <BetriebsstellenartField
                        {...field}
                        label={t('auftraege.24_11.betriebsstellenart_label')}
                        variant="solid"
                        className="w-full"
                        selectClassName="w-full"
                        errors={errors}
                        disabled={isDisabled}
                        readOnly={isReadOnly}
                        data-testid={`${testId}-feld-24-13-art-inner`}
                      />
                    )}
                  />
                }
                bestriebsstelleField={
                  <BefehlFormField
                    name="auftrag_24_11_12_fields.feld24_13.name"
                    control={control}
                    label={t('auftraege.24_11.zugmeldestelle_label')}
                    readOnly={readOnly}
                    disabled={false}
                    activateEmptyWarning={activateEmptyFieldWarnings}
                    useTextDisplayInReadOnly={true}
                    data-testid={`${testId}-feld-24-13-name`}
                    render={({ field, isDisabled, isReadOnly }) => (
                      <BetriebsstelleField
                        {...field}
                        label={t('auftraege.24_11.zugmeldestelle_label')}
                        errors={fieldErrors}
                        disabled={isDisabled}
                        readOnly={isReadOnly}
                        typ={sel24_13Betriebsstellenart}
                        data-testid={`${testId}-feld-24-13-name-inner`}
                      />
                    )}
                  />
                }
              />
            </BorderedBox>
          </div>

          <div
            data-testid={`${testId}-text-in-richtung-div`}
            className="lg:text-right"
          >
            <span data-testid={`${testId}-text-in-richtung-span`}>
              {t('auftraege.24_11.richtung')}
            </span>
          </div>
          <div data-testid={`${testId}-feld-24-14-div`}>
            <BorderedBox
              data-testid={`${testId}-feld-24-14-bordered-box`}
              footer={
                <span data-testid={`${testId}-feld-24-14-label`}>
                  {t('auftraege.24_11.24_14_zugmeldestelle')}
                </span>
              }
            >
              <ZugmeldestelleWrapper
                betriebsstellenartField={
                  <BefehlFormField
                    name="auftrag_24_11_12_fields.feld24_14.art"
                    control={control}
                    label="Betriebsstellenart"
                    readOnly={readOnly}
                    disabled={false}
                    activateEmptyWarning={activateEmptyFieldWarnings}
                    useTextDisplayInReadOnly={true}
                    data-testid={`${testId}-feld-24-14-art`}
                    render={({ field, isDisabled, isReadOnly }) => (
                      <BetriebsstellenartField
                        {...field}
                        label={t('auftraege.24_11.betriebsstellenart_label')}
                        variant="solid"
                        className="w-full"
                        selectClassName="w-full"
                        errors={fieldErrors}
                        disabled={isDisabled}
                        readOnly={isReadOnly}
                        data-testid={`${testId}-feld-24-14-art-inner`}
                      />
                    )}
                  />
                }
                bestriebsstelleField={
                  <BefehlFormField
                    name="auftrag_24_11_12_fields.feld24_14.name"
                    control={control}
                    label={t('auftraege.24_11.zugmeldestelle_label')}
                    readOnly={readOnly}
                    disabled={false}
                    activateEmptyWarning={activateEmptyFieldWarnings}
                    useTextDisplayInReadOnly={true}
                    data-testid={`${testId}-feld-24-14-name`}
                    render={({ field, isDisabled, isReadOnly }) => (
                      <BetriebsstelleField
                        {...field}
                        label={t('auftraege.24_11.zugmeldestelle_label')}
                        className="w-full"
                        errors={fieldErrors}
                        disabled={isDisabled}
                        readOnly={isReadOnly}
                        typ={sel24_14Betriebsstellenart}
                        data-testid={`${testId}-feld-24-14-name-inner`}
                      />
                    )}
                  />
                }
              />
            </BorderedBox>
          </div>

          <div data-testid={`${testId}-text-bis`} className="lg:text-right">
            <span data-testid={`${testId}-text-bis-span`}>
              {t('auftraege.24_11.bis')}
            </span>
          </div>
          <div
            className="flex flex-wrap items-baseline gap-2"
            data-testid={`${testId}-feld-24-15-16-div`}
          >
            <BefehlFormField
              name="auftrag_24_11_12_fields.feld_24_15_16_type"
              control={control}
              label=""
              className="w-full"
              readOnly={false}
              disabled={false}
              activateEmptyWarning={false}
              useTextDisplayInReadOnly={false}
              data-testid={`${testId}-feld-24-15-16-selector`}
              render={({ field, isDisabled }) => (
                <AuftragOptionen
                  name="auftrag_24_11_12_fields.feld_24_15_16_type"
                  value={field.value}
                  onChange={(newValue) => on24_15_16TypeChange(newValue)}
                  readOnly={readOnly}
                  disabled={isDisabled}
                  errors={fieldErrors}
                  data-testid={`${testId}-feld-24-15-16-selector-options`}
                >
                  <AuftragOption value="km" label={`24.15 km`}>
                    <div className="w-full">
                      <BefehlFormField
                        name="auftrag_24_11_12_fields.feld_24_15_km"
                        control={control}
                        label={t('auftraege.24_11.km_label')}
                        readOnly={readOnly}
                        disabled={feld_24_15_16_type !== 'km'}
                        activateEmptyWarning={activateEmptyFieldWarnings}
                        data-testid={`${testId}-feld-24-15`}
                        render={({ field: kmField, isDisabled }) => (
                          <Input
                            {...kmField}
                            label={t('auftraege.24_11.km_label')}
                            disabled={isDisabled}
                            errors={fieldErrors}
                            data-testid={`${testId}-feld-24-15-inner`}
                          />
                        )}
                      />
                    </div>
                  </AuftragOption>
                  <AuftragOption value="signal" label="24.16 Signal">
                    <div className="w-full">
                      <BefehlFormField
                        name="auftrag_24_11_12_fields.feld_24_16_signal"
                        control={control}
                        label={t('auftraege.24_11.signal_label')}
                        readOnly={readOnly}
                        disabled={feld_24_15_16_type !== 'signal'}
                        activateEmptyWarning={activateEmptyFieldWarnings}
                        data-testid={`${testId}-feld-24-16`}
                        render={({ field: signalField, isDisabled }) => (
                          <Input
                            {...signalField}
                            label={t('auftraege.24_11.signal_label')}
                            disabled={isDisabled}
                            errors={fieldErrors}
                            data-testid={`${testId}-feld-24-16-inner`}
                          />
                        )}
                      />
                    </div>
                  </AuftragOption>
                </AuftragOptionen>
              )}
            />
          </div>
        </div>
        <div className="mb-1" data-testid={`${testId}-und-kehrt-zurueck-div`}>
          <span data-testid={`${testId}-und-kehrt-zurueck-span`}>
            {t('auftraege.24_11.zurueck')}
          </span>
        </div>
      </div>
    </div>
  )
}
